import * as THREE from "three";
import { select } from "../core/dom";

interface WaveCanvasOptions {
  color: string;
  particleCount: number;
  cameraY: number;
  cameraZ: number;
  rotationX?: number;
  rotationZ?: number;
  waveHeight: number;
  speed: number;
}

interface WaveCanvasDefinition extends WaveCanvasOptions {
  selector: string;
}

const DEFAULT_ROTATION_X = -0.35;
const DEFAULT_ROTATION_Z = -0.08;

class WaveCanvas {
  private readonly mount: HTMLElement;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly geometry: THREE.BufferGeometry;
  private readonly material: THREE.ShaderMaterial;
  private readonly points: THREE.Points;
  private readonly baseRotationZ: number;
  private readonly resizeObserver: ResizeObserver;
  private readonly visibilityObserver: IntersectionObserver;

  private elapsedTime = 0;
  private visible = true;

  constructor(mount: HTMLElement, options: WaveCanvasOptions) {
    this.mount = mount;
    this.camera = new THREE.PerspectiveCamera(60, 1, 1, 2000);
    this.camera.position.set(0, options.cameraY, options.cameraZ);

    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.domElement.style.pointerEvents = "none";
    this.renderer.domElement.style.touchAction = "none";
    this.renderer.domElement.style.width = "100%";
    this.renderer.domElement.style.height = "100%";
    this.renderer.domElement.style.display = "block";
    this.mount.append(this.renderer.domElement);

    this.geometry = this.createGeometry(options.particleCount);
    this.material = this.createMaterial(options);
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.rotation.x = options.rotationX ?? DEFAULT_ROTATION_X;
    this.baseRotationZ = options.rotationZ ?? DEFAULT_ROTATION_Z;
    this.points.rotation.z = this.baseRotationZ;
    this.scene.add(this.points);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(this.mount);

    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.visible = entry?.isIntersecting ?? false;
      },
      { threshold: 0.05 }
    );
    this.visibilityObserver.observe(this.mount);

    this.resize();
  }

  get isVisible(): boolean {
    return this.visible;
  }

  render(deltaSeconds: number, animate: boolean): void {
    if (animate) {
      this.elapsedTime += deltaSeconds;
      this.material.uniforms.uTime!.value = this.elapsedTime;
      this.points.rotation.z = this.baseRotationZ + Math.sin(this.elapsedTime * 0.15) * 0.04;
    }

    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.resizeObserver.disconnect();
    this.visibilityObserver.disconnect();
    this.geometry.dispose();
    this.material.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }

  private resize(): void {
    const width = Math.round(this.mount.clientWidth);
    const height = Math.round(this.mount.clientHeight);
    if (!width || !height) return;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.setSize(width, height, false);
  }

  private createGeometry(particleCount: number): THREE.BufferGeometry {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const scales = new Float32Array(particleCount);
    const width = 1000;
    const depth = 1000;

    for (let index = 0; index < particleCount; index += 1) {
      const positionIndex = index * 3;
      positions[positionIndex] = Math.random() * width - width / 2;
      positions[positionIndex + 1] = 0;
      positions[positionIndex + 2] = Math.random() * depth - depth / 2;
      scales[index] = Math.random();
    }

    geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute("aScale", new THREE.BufferAttribute(scales, 1));
    return geometry;
  }

  private createMaterial(options: WaveCanvasOptions): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: new THREE.Color(options.color) },
        uWaveHeight: { value: options.waveHeight },
        uSpeed: { value: options.speed },
      },
      vertexShader: `
        uniform float uTime;
        uniform float uWaveHeight;
        uniform float uSpeed;

        attribute float aScale;
        varying float vAlpha;

        void main() {
          vec3 pos = position;
          float t = uTime * uSpeed;
          float wave1 = sin((pos.x * 0.016) + (t * 0.9)) * uWaveHeight;
          float wave2 = cos((pos.z * 0.014) + (t * 0.7)) * uWaveHeight;

          pos.y = wave1 + wave2;

          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_Position = projectionMatrix * mvPosition;
          gl_PointSize = (3.2 + aScale * 2.8) * (280.0 / -mvPosition.z);
          vAlpha = 0.72 + aScale * 0.28;
        }
      `,
      fragmentShader: `
        uniform vec3 uColor;
        varying float vAlpha;

        void main() {
          float dist = distance(gl_PointCoord, vec2(0.5));
          float strength = 1.0 - smoothstep(0.22, 0.5, dist);
          strength = pow(strength, 1.35);
          gl_FragColor = vec4(uColor, strength * vAlpha);
        }
      `,
    });
  }
}

const DEFINITIONS: WaveCanvasDefinition[] = [
  {
    selector: "#contact-wave-canvas",
    color: "#8cecff",
    particleCount: 7000,
    cameraY: 95,
    cameraZ: 230,
    waveHeight: 22,
    speed: 0.9,
  },
  {
    selector: "#footer-wave-canvas",
    color: "#8cecff",
    particleCount: 5500,
    cameraY: 120,
    cameraZ: 260,
    waveHeight: 18,
    speed: 0.75,
  },
  {
    selector: "#welcome-wave-canvas",
    color: "#000000",
    particleCount: 12000,
    cameraY: 70,
    cameraZ: 180,
    waveHeight: 30,
    speed: 0.55,
  },
  ...[
    "#ecocube-next-wave",
    "#alphawave-next-wave",
    "#muonio-next-wave",
    "#rootline-next-wave",
    "#adhd-game-next-wave",
    "#nucleus-next-wave",
    "#wavebreaker-next-wave",
    "#weather-next-wave",
    "#c32-next-wave",
    "#clarity-next-wave",
    "#codaroxai-next-wave",
    "#smeco-next-wave",
    "#floorcollapse-next-wave",
  ].map((selector) => ({
    selector,
    color: "#8cecff",
    particleCount: 8000,
    cameraY: 90,
    cameraZ: 210,
    waveHeight: 22,
    speed: 0.8,
  })),
];

export function initWaveCanvases(): void {
  const waves: WaveCanvas[] = [];

  for (const definition of DEFINITIONS) {
    const mount = select<HTMLElement>(definition.selector);
    if (!mount) continue;

    try {
      waves.push(new WaveCanvas(mount, definition));
    } catch (error) {
      console.warn(`[portfolio] WebGL wave unavailable for ${definition.selector}.`, error);
    }
  }

  if (waves.length === 0) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let animationFrame = 0;
  let previousTime = performance.now();

  const frame = (now: number): void => {
    const delta = Math.min((now - previousTime) / 1000, 0.033);
    previousTime = now;

    if (!document.hidden) {
      for (const wave of waves) {
        if (wave.isVisible) wave.render(delta, !reduceMotion.matches);
      }
    }

    animationFrame = requestAnimationFrame(frame);
  };

  animationFrame = requestAnimationFrame(frame);

  window.addEventListener("pagehide", (event) => {
    if (event.persisted) return;
    cancelAnimationFrame(animationFrame);
    waves.forEach((wave) => wave.dispose());
  });
}
